import React from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { lighten } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';
import { AppBar, Toolbar, Hidden, IconButton, Typography } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import StripeGetPremiumModal from 'views/premium/StripeGetPremiumModal';
import { isAccountTypePaid } from 'commons/utils/accountUtils';
import HeaderPremium from './HeaderPremium';
import HeaderNotPremium from './HeaderNotPremium';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DisplayIfRolesIsPresent from 'components/authorization/DisplayIfRolesIsPresent';
import { BR_TIMEZONES, ROLES_ALL_SECRETARY_AND_ADMIN } from 'commons/constants';
import ChipStatus from 'components/chips/ChipStatus';
import { currentUserSelector, resetStore, useAppStore } from 'stores/appStore';

const drawerWidth = 240;

const useStyles = makeStyles()((theme) => ({
  root: {
    display: 'flex',
  },
  appBar: {
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),

    // boxShadow:
    //   '2px 0px 1px -1px rgb(0 0 0 / 20%), 1px 0px 1px 0px rgb(0 0 0 / 14%), 1px 0px 3px 0px rgb(0 0 0 / 12%',

    // background: process.env.REACT_APP_STAGE
    //   ? `linear-gradient(45deg, rgb(183 26 26) 0%, #e58221 20%, #8C21E5  100%)`
    //   : `linear-gradient(45deg, #8C21E5 0%, ${lighten('#8C21E5', 0.1)} 60%, #5721E5  100%)`,
    background: `linear-gradient(45deg, #8C21E5 0%, ${lighten('#8C21E5', 0.1)} 60%, #5721E5  100%)`,
  },
  appBarShift: {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  flexGrow: {
    flexGrow: 1,
  },
  signOutButton: {
    marginLeft: theme.spacing(1),
  },
  logo: {
    width: '50px',
    borderRadius: '50%',
  },
  name: {
    color: '#ffffffd4',
    padding: '0px 10px',
    marginBottom: '5px',
    fontSize: '16px',
    fontWeight: 'bold',
  },
  avatar: {
    width: 33,
    height: 33,
    fontSize: '14px',
    borderWidth: '2px',
    borderStyle: 'solid',
    marginLeft: '1rem',
  },
  labelLoggedInUser: {
    color: 'white',
    marginLeft: '0.5rem',
    '@media (min-height:710px)': {
      display: 'none',
    },
  },
}));

const isStaged = process.env.REACT_APP_STAGE;
const COLOR_WHITE = { color: 'white' };

// COMPONENT
function Topbar({ className, onToggleMenu, open }) {
  const { classes, cx } = useStyles();
  const currentUser = useAppStore(currentUserSelector);
  const navigate = useNavigate();

  const isBr = React.useMemo(() => {
    try {
      const timeZoneString = Intl.DateTimeFormat().resolvedOptions().timeZone;
      return BR_TIMEZONES.includes(timeZoneString);
    } catch (e) {
      console.log(e);
      // silently ignore error
      return true;
    }
  }, []);

  const handleLogout = () => {
    navigate('/login', { replace: true });
    resetStore();
  };

  return (
    <AppBar position="absolute" className={classes.appBar}>
      <StripeGetPremiumModal />
      <Toolbar>
        <IconButton
          edge="start"
          color="inherit"
          aria-label="open drawer"
          onClick={onToggleMenu}
          className={cx(classes.menuButton, open && classes.menuButtonHidden)}
          size="large"
        >
          <MenuIcon sx={COLOR_WHITE} />
        </IconButton>
        <RouterLink to="/">
          <img alt="Logo" src="/images/logos/minha-agenda-logo-white-small.png" className={classes.logo} />
        </RouterLink>
        <Hidden lgDown>
          <RouterLink to="/">
            <Typography className={classes.name} variant="h6">
              MinhaAgenda
            </Typography>
          </RouterLink>
          {isStaged && <ChipStatus label="STAGED" status="warning" />}
        </Hidden>
        <div className={classes.flexGrow} />
        {isAccountTypePaid(currentUser) ? (
          <>
            <DisplayIfRolesIsPresent requiredRoles={ROLES_ALL_SECRETARY_AND_ADMIN}>
              <HeaderPremium />
            </DisplayIfRolesIsPresent>
          </>
        ) : (
          <>{isBr && <HeaderNotPremium />}</>
        )}

        {/* LEFT SIDE */}
        <Typography variant="body1" className={classes.labelLoggedInUser}>
          Olá, {currentUser?.principal?.name}
        </Typography>

        <Hidden smDown>
          {/* <Avatar
            style={{
              backgroundColor: getAgendaColor(currentUser.principal.agendaColorId).base,
              borderColor: getAgendaColor(currentUser.principal.agendaColorId).header,
            }}
            className={classes.avatar}
          >
            {getInitials(currentUser.principal.name)}
          </Avatar> */}
          <IconButton className={classes.signOutButton} color="inherit" onClick={handleLogout} size="large">
            <FontAwesomeIcon icon="right-from-bracket" size="1x" fixedWidth color="white" />
          </IconButton>
        </Hidden>

        {/* LEFT SIDE */}
      </Toolbar>
    </AppBar>
  );
}

Topbar.propTypes = {
  className: PropTypes.string,
  onSidebarOpen: PropTypes.func,
};

export default Topbar;
